import { useMemo } from 'react';
import type { PosCartItem } from '../../domain/models/PosCartItem';
import { cn } from '@/shared/utils/cn';
import { InStorePicksIcon, WishlistedBadgeIcon, FromYourCartBadgeIcon } from '@/shared/icons/icons';
import { ProductCard } from '@/features/plp/presentation/components/ProductCard';
import type { ListingDesignItem } from '@/features/plp/domain/models/ProductListing';
import { mapPosCartItemToListingDesignItem } from '../../utils/posCartUtils';

export interface StoreSelectionCardProps {
  product: PosCartItem;
  onWishlistToggle?: (product: PosCartItem, isWishlisted: boolean) => void;
  onAddToCart?: (product: PosCartItem) => void;
  onGoToCart?: (product: PosCartItem) => void;
  onProductClick?: (product: PosCartItem) => void;
  hideCta?: boolean;
  isPdp?: boolean;
  className?: string;
}

export default function StoreSelectionCard({
  product,
  onWishlistToggle,
  onAddToCart,
  onGoToCart,
  onProductClick,
  hideCta = false,
  isPdp = false,
  className,
}: StoreSelectionCardProps) {
  const isGoToCart = product.sourceType === 'WEB_CART';

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isGoToCart) {
      if (onGoToCart) onGoToCart(product);
    } else {
      if (onAddToCart) onAddToCart(product);
    }
  };

  const handleCardClick = () => {
    if (onProductClick) {
      onProductClick(product);
    }
  };

  const handleWishlistToggle = (_item: ListingDesignItem, action: 'add' | 'remove') => {
    if (onWishlistToggle) {
      onWishlistToggle(product, action === 'add');
    }
  };

  const renderBadge = () => {
    switch (product.sourceType) {
      case 'POS_CART':
      case 'POS_VIEWED':
        return (
          <div
            className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 z-10 flex items-center gap-1 rounded px-[6px] py-[4px] text-[12px] font-bold text-[#103064] shadow-sm"
            style={{ background: 'linear-gradient(90deg, #E5F4EF 0%, #EFF4DE 25.48%, #EFEAEE 49.52%, #DDF5F9 91.83%)' }}
          >
            <InStorePicksIcon className="h-3 w-3 shrink-0" />
            <span>Your In-Store Picks</span>
          </div>
        );
      case 'WEB_WISHLIST':
        return (
          <div
            className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 z-10 flex items-center gap-1 rounded px-[6px] py-[4px] text-[12px] font-bold text-[#765059] shadow-sm "
            style={{ background: 'linear-gradient(90deg, #FFF4F4 0%, #F9D1DC 100%)' }}
          >
            <WishlistedBadgeIcon className="h-3 w-3 shrink-0" />
            <span>Wishlisted</span>
          </div>
        );
      case 'WEB_CART':
        return (
          <div
            className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 z-10 flex items-center gap-1 rounded px-[6px] py-[4px] text-[12px] font-bold text-black shadow-sm "
            style={{ background: 'linear-gradient(90deg, #FFE2E0 0%, #F9D1DC 100%)' }}
          >
            <FromYourCartBadgeIcon className="h-3 w-3 shrink-0" />
            <span>From Your Cart</span>
          </div>
        );
      default:
        return null;
    }
  };

  const mappedProduct = useMemo(() => mapPosCartItemToListingDesignItem(product), [product]);

  return (
    <ProductCard
      product={mappedProduct}
      className={cn(
        "rounded-[8px] border border-[#E5E9ED] bg-white transition-all duration-300",
        isPdp
          ? "w-[calc((100vw-30px)/2.33)] md:w-[calc((100vw-97px)/4.33)] lg:w-[265.7px] flex-shrink-0"
          : "w-[calc(0.9*(100vw-36px)/2)] md:w-[calc(0.9*(100vw-56px)/3)] lg:w-[calc(0.9*(100vw-68px)/4)] flex-shrink-0",
        className
      )}
      badge={renderBadge()}
      footerCta={!hideCta && (
        <button
          type="button"
          onClick={handleButtonClick}
          className="w-full rounded-[6px] sm:rounded-[8px] py-2 sm:py-2.5 text-center text-[14px] font-bold tracking-wider transition-all duration-200 border border-[#D0D8E4] bg-white text-[#103064] hover:bg-[#011E39] hover:text-white normal-case"
        >
          {isGoToCart ? 'Go to Cart' : 'Add to Cart'}
        </button>
      )}
      onProductClick={handleCardClick}
      onWishlistToggle={handleWishlistToggle}
      openInNewTab={typeof window !== 'undefined' && window.innerWidth >= 768}
      hideVideoCallButton
      hideViewDetailsButton
    />
  );
}
